<script>
  import { onMount } from 'svelte';
  import { getProjects } from '../../../services/project';
  
  export let multiMaps = [];
  export let updateConfig;
  
  let allProjects = [];
  let showAddModal = false;
  let selectedProject = '';
  let customLabel = '';
  let editingIndex = -1;
  
  onMount(async () => {
    try {
      const projectsStore = await getProjects();
      // Handle both store response and direct array
      if (projectsStore && projectsStore.subscribe) {
        // It's a store
        projectsStore.subscribe(projects => {
          allProjects = projects || [];
        });
      } else if (Array.isArray(projectsStore)) {
        // It's an array
        allProjects = projectsStore;
      } else if (projectsStore && projectsStore.data) {
        // It's an object with data property
        allProjects = projectsStore.data || [];
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
      allProjects = [];
    }
  });
  
  function addMultiMap() {
    if (!selectedProject) return;
    
    const project = allProjects.find(p => p._id === selectedProject);
    if (!project) return;
    
    const newMap = {
      label: customLabel || project.name,
      value: selectedProject
    };
    
    if (editingIndex >= 0) {
      // Edit existing
      const updated = [...multiMaps];
      updated[editingIndex] = newMap;
      updateConfig('multiMaps', updated);
      editingIndex = -1;
    } else {
      // Add new
      updateConfig('multiMaps', [...multiMaps, newMap]);
    }
    
    resetForm();
  }
  
  function editMultiMap(index) {
    const map = multiMaps[index];
    selectedProject = map.value;
    customLabel = map.label;
    editingIndex = index;
    showAddModal = true;
  }
  
  function removeMultiMap(index) {
    const updated = multiMaps.filter((_, i) => i !== index);
    updateConfig('multiMaps', updated);
  }
  
  function resetForm() {
    selectedProject = '';
    customLabel = '';
    showAddModal = false;
    editingIndex = -1;
  }
  
  function getProjectName(value) {
    const project = allProjects.find(p => p._id === value);
    return project ? project.name : 'Unknown Project';
  }
</script>

<div class="multi-maps-editor">
  <h4>Multi Maps</h4>
  
  {#if multiMaps && multiMaps.length > 0}
    <div class="maps-list">
      {#each multiMaps as map, index}
        <div class="map-item">
          <div class="map-info">
            <strong>{map.label}</strong>
            <span class="project-ref">({getProjectName(map.value)})</span>
          </div>
          <div class="map-actions">
            <button class="btn btn-small" on:click={() => editMultiMap(index)}>Edit</button>
            <button class="btn btn-small btn-danger" on:click={() => removeMultiMap(index)}>Remove</button>
          </div>
        </div>
      {/each}
    </div>
  {:else}
    <p class="no-maps">No multi maps configured</p>
  {/if}
  
  <button class="btn btn-secondary" on:click={() => showAddModal = true}>
    + Add Multi Map
  </button>
  
  {#if showAddModal}
    <div class="modal-overlay" on:click={resetForm}>
      <div class="modal-content" on:click|stopPropagation>
        <h3>{editingIndex >= 0 ? 'Edit' : 'Add'} Multi Map</h3>
        
        <div class="form-styled">
          <div class="field-wrapper">
            <label>Project</label>
            <select bind:value={selectedProject}>
              <option value="">Select a project...</option>
              {#each allProjects as project}
                <option value={project._id}>{project.name}</option>
              {/each}
            </select>
          </div>
          
          <div class="field-wrapper">
            <label>Custom Label (optional)</label>
            <input 
              type="text"
              bind:value={customLabel}
              placeholder="Leave empty to use project name"
            />
          </div>
          
          <div class="modal-actions">
            <button class="btn" on:click={resetForm}>Cancel</button>
            <button 
              class="btn btn-primary" 
              on:click={addMultiMap}
              disabled={!selectedProject}
            >
              {editingIndex >= 0 ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>

<style lang="scss">
  .multi-maps-editor {
    margin-bottom: 15px;
    
    h4 {
      margin-bottom: 10px;
      font-size: 0.85rem;
      font-weight: bold;
    }
  }
  
  .maps-list {
    margin-bottom: 10px;
  }
  
  .map-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px;
    background: #e5e7eb;
    border-radius: 4px;
    margin-bottom: 5px;
    
    .map-info {
      flex: 1;
      
      .project-ref {
        font-size: 0.7rem;
        color: #6b7280;
        margin-left: 5px;
      }
    }
    
    .map-actions {
      display: flex;
      gap: 4px;
    }
  }
  
  .no-maps {
    font-size: 0.75rem;
    color: #6b7280;
    margin-bottom: 10px;
    font-style: italic;
  }
  
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 60;
  }
  
  .modal-content {
    background: #f6f6f6;
    border-radius: 10px;
    padding: 20px;
    width: 400px;
    max-width: 90%;
    border: 4px solid #d4d3bc;
    
    h3 {
      margin-bottom: 15px;
    }
  }
  
  .field-wrapper {
    margin-bottom: 15px;
    
    label {
      display: inline-flex;
      font-weight: bold;
      margin-bottom: 5px;
      font-size: 0.75rem;
    }
    
    input, select {
      width: 100%;
      padding: 8px;
      border: 1px solid #d4d3bc;
      border-radius: 4px;
      background: white;
    }
  }
  
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 20px;
  }
  
  .btn {
    padding: 6px 12px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: bold;
    
    &.btn-small {
      padding: 4px 8px;
      font-size: 12px;
    }
    
    &.btn-secondary {
      background: #10b981;
      color: white;
      
      &:hover {
        background: #059669;
      }
    }
    
    &.btn-primary {
      background: #3b82f6;
      color: white;
      
      &:hover {
        background: #2563eb;
      }
    }
    
    &.btn-danger {
      background: #ef4444;
      color: white;
      
      &:hover {
        background: #dc2626;
      }
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
</style>
